import { ChangeEvent, FocusEventHandler, memo } from "react";
import { uniqueId } from "lodash-es";
import styled from "styled-components";

import ErrorStyle from "@/shared/ui/ErrorStyle";
import { Label } from "@/shared/ui/Label";

export type FormInputValue = string | number | boolean | string[];

interface FormInputProps<T> {
  className?: string;

  title?: string;
  /**
   * The text label for this input field.
   */
  label?: string;
  /**
   * The type HTML attribute passed to the input HTML element.
   */
  type?: string;
  /**
   * Callback used when the text input changes. The entire model is passed in, with the value for this text input updated.
   */
  // eslint-disable-next-line
  changeModel?: Function;
  /**
   * The key in model for this text input value.
   */
  modelKey?: string;
  /**
   * The dictionary of form data that this text field is a part of.
   */
  model?: T | Record<string, FormInputValue>;
  /**
   * The dictionary of errors for the form this text field is a part of. Shows an error for this radio input if there is an error with key modelKey.
   */
  errors?: Record<string, string>;
  /**
   * Whether label should be bold.
   */
  boldLabel?: boolean;
  /**
   * Placeholder text shown if this field is empty.
   */
  placeholder?: string;
  /**
   * If true, text cannot be changed.
   */
  disabled?: boolean;
  /**
   * A regexp pattern that the input must match. If an input change violates this pattern, the text will not be changed.
   */
  pattern?: RegExp;
  /**
   * If true, automatically inserts a slash after 2 numbers are entered, for date input fields.
   */
  autoslash?: boolean;
  /**
   * The inputmode HTML attribute passed to the input element. Restricts input characters.
   */
  inputmode?: "text" | "search" | "none" | "tel" | "url" | "email" | "numeric" | "decimal";

  onFocus?: FocusEventHandler;
}

/**
 * A one-line text input field, as part of a form.
 *
 * @example ../docs/TextFormInput.md
 */
function TextFormInput<T>({
  className,
  title,
  label,
  type,
  changeModel,
  modelKey,
  model,
  errors,
  placeholder,
  disabled,
  pattern,
  autoslash = false,
  inputmode = "text",
  onFocus,
}: FormInputProps<T>) {
  const errorId = uniqueId("errorLabel");

  const value = model && modelKey ? model[modelKey] : undefined;
  const error = errors && modelKey ? errors[modelKey] : undefined;

  function handleOnChange(e: ChangeEvent<HTMLInputElement>) {
    let newValue = e.target.value;
    const currentValue = (value as string) || "";

    if (autoslash) {
      // Only add a slash if going from M -> MM or MM/D -> MM/DD
      if (
        (currentValue.match(/^\d$/) && newValue.match(/^\d{2}$/)) ||
        (currentValue.match(/^\d{2}\/\d$/) && newValue.match(/^\d{2}\/\d{2}$/))
      ) {
        const slashAdded = `${newValue}/`;
        // Do not add slash if it violates the pattern (e.g. prevent MM/DD/ for MM/DD)
        if (!pattern || slashAdded.match(pattern)) {
          newValue = slashAdded;
        }
      }

      // Do not allow / to be typed after /
      if (currentValue.slice(-1) === "/" && newValue === `${currentValue}/`) {
        return;
      }
    }

    if (changeModel && modelKey && (!pattern || newValue.match(pattern))) {
      changeModel({ ...model, [modelKey]: newValue });
    }
  }

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <FormInputTextField
        title={title}
        type={type}
        inputMode={inputmode}
        onChange={handleOnChange}
        onFocus={onFocus}
        value={value as string}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        aria-invalid={!!error}
        aria-errormessage={errorId}
      />
      {error && <ErrorStyle id={errorId}>{error}</ErrorStyle>}
    </div>
  );
}

export default memo(TextFormInput);

export const FormInputTextField = styled("input")<{ error?: string }>`
  font: var(--font-xs);
  height: 36px;
  margin-top: var(--spacing-2);
  background: var(--color-input-bg);
  color: var(--color-input-text);
  border-radius: 4px;
  border: 1px solid ${(p) => (p.error ? "red" : "var(--color-input-border)")};
  padding: var(--spacing-3);
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
  }
`;
